/**
 * Whether a text field holds its own words or borrows them from the partner's
 * translation catalog — and how to move it between the two.
 *
 * A field is one of exactly two things:
 *
 * - **literal** — the words are in the template. One language, wherever the template
 *   is sent.
 * - **bound** — the field is `{{ 'group.key'|trans }}` and the words live in
 *   `partner_translations`, per language. The same key can be reused by every
 *   template that says the same thing, and the wording is edited in one place.
 *
 * Everything here is pure so the rules can be tested without a DOM, and shared by
 * the inspector, the subject/pretext fields and (later) the inline editor's
 * translation atom.
 *
 * **Why "the whole value or nothing".** A partially-bound field
 * (`Hello {{ 'x'|trans }}`) is *not* treated as bound: Twig does not re-parse a
 * string argument, so a component that receives `'Hello ' ~ x` renders the
 * concatenation while one that receives the literal renders the braces as text. The
 * editor therefore offers one binding per field, which is the case that always works
 * — and a mixed value stays a literal, which is what it is.
 */

/** Group and key as `partner_translations` stores them (`translation_group`, `key`). */
export interface TranslationKeyParts {
  group: string;
  key: string;
}

export type TranslationBinding =
  /** The field's own words. */
  | { kind: 'literal'; text: string }
  /**
   * The words come from `key`. `raw` records `|raw` after `|trans`, which matters:
   * dropping it would start escaping HTML a partner had deliberately written.
   */
  | { kind: 'bound'; key: string; raw: boolean };

/**
 * A whole value that is one `trans` expression. Anchored, so anything around it
 * (`Hello {{ 'x'|trans }}`) fails to match and stays literal — see the header.
 *
 * Accepts either quote style and an optional `|raw`, because both appear in the
 * partner templates this has to read back.
 */
const WHOLE_BINDING =
  /^\{\{\s*(['"])([^'"\n]+)\1\s*\|\s*trans\s*(\|\s*raw\s*)?\}\}$/;

export function parseBinding(value: unknown): TranslationBinding {
  const text = typeof value === 'string' ? value : '';
  const match = WHOLE_BINDING.exec(text.trim());
  if (!match) return { kind: 'literal', text };

  return { kind: 'bound', key: match[2], raw: Boolean(match[3]) };
}

/** The Twig a bound field stores. Single quotes, to match what `compile()` emits. */
export function bindingExpression(key: string, raw = false): string {
  return `{{ '${key}'|trans${raw ? '|raw' : ''} }}`;
}

/**
 * Splits a full key the way the API stores it: **first segment is the group**, the
 * rest is the key.
 *
 * Not "split on every dot": real keys are `promotion.image.first.src` in group
 * `promotion`, so anything else would create a second group per key and scatter a
 * partner's catalog. A key with no dot gets the `emails` group rather than an empty
 * one, which the API would reject.
 */
export function keyParts(fullKey: string): TranslationKeyParts {
  const trimmed = fullKey.trim();
  const dot = trimmed.indexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) {
    return { group: DEFAULT_GROUP, key: trimmed };
  }
  return { group: trimmed.slice(0, dot), key: trimmed.slice(dot + 1) };
}

/** Where minted keys go, so a partner's own groups stay theirs. */
export const DEFAULT_GROUP = 'emails';

/** Lowercase, dot-separated, alphanumeric-and-underscore only. */
function slugify(part: string): string {
  return part
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * A key for a field that has none yet: `emails.<template>.<field>`.
 *
 * Derived from names the user already recognises rather than a random id, because this
 * key becomes permanent, is shown in the picker, and is what the next template reuses.
 * A missing or unslugifiable part is dropped instead of leaving `__` in the middle —
 * and with nothing usable at all it returns null, so the caller asks instead of minting
 * `emails..`.
 *
 * It is only ever a **suggestion**: the field lets the group and the key be chosen or
 * typed, because a name derived from a long template title
 * (`emails.zz_xxxl_appointment_all_regions_keep.betreff`) is not one anybody would
 * pick, and the key outlives the template that first needed it.
 */
export function suggestKey(
  templateName: string | null | undefined,
  fieldName: string | null | undefined,
): string | null {
  const parts = [templateName, fieldName]
    .map((part) => slugify(part ?? ''))
    .filter(Boolean);

  if (parts.length === 0) return null;
  return [DEFAULT_GROUP, ...parts].join('.');
}

/** The key part of a suggestion, slugified — what the key input is pre-filled with. */
export function suggestKeyName(
  templateName: string | null | undefined,
  fieldName: string | null | undefined,
): string {
  const parts = [templateName, fieldName]
    .map((part) => slugify(part ?? ''))
    .filter(Boolean);
  return parts.join('.');
}

/** Normalises a hand-typed group or key part: trimmed, lowercase, no stray dots. */
export function normalizeKeyPart(part: string): string {
  return part
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/^\.+|\.+$/g, '');
}

/**
 * A key that does not collide with one already in the catalog, by suffixing `_2`,
 * `_3`, … — two fields called "Text" on the same template are ordinary, and silently
 * pointing both at one key would make editing either change both.
 */
export function uniqueKey(candidate: string, taken: Iterable<string>): string {
  const existing = new Set(taken);
  if (!existing.has(candidate)) return candidate;

  for (let n = 2; n < 1000; n += 1) {
    const next = `${candidate}_${n}`;
    if (!existing.has(next)) return next;
  }
  return `${candidate}_${Date.now()}`;
}

/** What a bound field shows, and whether the active language actually has words. */
export interface ResolvedBinding {
  text: string;
  missing: boolean;
}

/**
 * The wording for `language`, plus whether it is missing.
 *
 * An **empty string counts as present**: the server resolves an existing-but-empty
 * translation to `''` (which is how optional blocks are hidden), so treating it as
 * missing would nag about a value the partner set deliberately.
 */
export function resolveBinding(
  key: string,
  values: Record<string, string | undefined> | undefined,
): ResolvedBinding {
  const text = values?.[key];
  return text === undefined ? { text: '', missing: true } : { text, missing: false };
}
